import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { bookingService } from 'api/services/bookingService';
import { paymentService } from 'api/services/paymentService';
import { FilePreviewModal } from 'components/ui/FilePreviewModal';
import type { Booking, BookingFull, BookingDetail, BookingGuest } from 'api/types/booking.types';
import type { Payment } from 'api/types';
import ModalLoadingSkeleton from 'components/common/ModalLoadingSkeleton';
import notificationService from 'services/notificationService';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import PaymentCaptureModal from 'components/modals/modules/payment-module/PaymentCaptureModal';
import PaymentVerificationBadge from 'components/modules/payment-module/PaymentVerificationBadge';
import ReceiptNumber from 'components/common/billing/ReceiptNumber';
import BookingPdfPreviewModal from 'components/modals/modules/booking-module/BookingPdfPreviewModal';
import { formatDate, formatCurrency, formatTime, getStatusBadgeClass, BOOKING_SOURCE } from './booking.utils';
import { buildBookingTaxBreakdown, type TaxBreakdownRow } from 'utils/bookingTax';
import { settlementModeLabel } from 'api/types/settlement.types';
import { outputProfileService } from 'api/services/outputProfileService';
import { openPdf } from 'components/modals/modules/pms-module/IssueInvoicesModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface BookingViewModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onEdit?: (booking: Booking) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (bookingId: string) => void;
  /** Where a payment taken here counts: Reservations by default; the Front Office workspace says FRONT_OFFICE. */
  collectionPoint?: 'FRONT_OFFICE' | 'RESERVATIONS';
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.85rem' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// SECTION HEADER
// ============================================================================

const SectionHeader: React.FC<{ title: string; first?: boolean }> = ({ title, first }) => (
  <div style={{ marginTop: first ? 0 : '1.25rem', borderTop: first ? 'none' : '1px solid var(--border-color)', paddingTop: first ? 0 : '1rem' }}>
    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>{title}</h3>
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const BookingViewModal: React.FC<BookingViewModalProps> = ({
  isOpen,
  booking,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
  collectionPoint = 'RESERVATIONS',
}) => {
  const { minimize } = useModalMinimize();
  const [fullData, setFullData] = useState<BookingFull | null>(null);
  const [loading, setLoading] = useState(false);

  // Payments tab state
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [captureModalOpen, setCaptureModalOpen] = useState(false);
  const [editPaymentRecord, setEditPaymentRecord] = useState<Payment | null>(null);
  const [voidConfirm, setVoidConfirm] = useState<{ open: boolean; payment: Payment | null }>({ open: false, payment: null });
  const [isVoiding, setIsVoiding] = useState(false);

  const fetchPayments = useCallback(async () => {
    if (!booking) return;
    setPaymentsLoading(true);
    try {
      const res = await paymentService.getByBookingId(booking.bookingId, true);
      setPayments(res.data || []);
    } catch {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [booking]);

  useEffect(() => {
    if (isOpen && booking) fetchPayments();
  }, [isOpen, booking, fetchPayments]);

  const handlePaymentSaved = useCallback(async () => {
    setCaptureModalOpen(false);
    setEditPaymentRecord(null);
    await fetchPayments();
    // Re-fetch full booking to refresh advance_received / balance_due in the summary.
    if (booking) {
      try {
        const response = await bookingService.getFullBooking(booking.bookingId);
        setFullData(response.data);
      } catch { /* silent */ }
    }
  }, [fetchPayments, booking]);

  const handleVoidPayment = useCallback(async () => {
    if (!voidConfirm.payment) return;
    setIsVoiding(true);
    try {
      await paymentService.void(voidConfirm.payment.paymentId, voidConfirm.payment.rowVersion);
      notificationService.toast.success(`Payment of ₹${formatCurrency(voidConfirm.payment.amount)} voided.`);
      setVoidConfirm({ open: false, payment: null });
      await handlePaymentSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to void payment.';
      notificationService.toast.error(msg);
    } finally {
      setIsVoiding(false);
    }
  }, [voidConfirm, handlePaymentSaved]);

  // Fetch full booking data when modal opens
  useEffect(() => {
    if (!isOpen || !booking) {
      setFullData(null);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await bookingService.getFullBooking(booking.bookingId);
        if (!cancelled) setFullData(response.data);
      } catch {
        // silent — header data still available from booking prop
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [isOpen, booking]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !booking) return;
    minimize({
      id: `${tabId}-view-modal-${booking.bookingId}`,
      title: `View - ${booking.bookingNumber}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(booking.bookingId),
    });
    onClose();
  }, [tabId, moduleName, booking, minimize, onClose, onRestoreView]);

  // "Download PDF" now opens an in-app preview first; the cashier reviews
  // and downloads from inside the preview modal.
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  // In-app preview for ID document — null when closed, else the relativePath
  // of the doc to show. The shared FilePreviewModal fetches the bytes via
  // authenticated axios, so the path stays in the DB-stored form (no
  // pre-built download URL leaks into the markup).
  const [idDocPreviewPath, setIdDocPreviewPath] = useState<string | null>(null);
  const handleDownloadPdf = useCallback(() => {
    if (!booking) return;
    setPdfPreviewOpen(true);
  }, [booking]);

  if (!isOpen || !booking) return null;

  // Use fullData if available, fall back to booking prop for header fields
  const header = fullData?.booking ?? booking;
  const detailLines: BookingDetail[] = fullData?.detailLines ?? [];
  const guests: BookingGuest[] = fullData?.guests ?? [];
  const addons = fullData?.addons ?? [];
  const primaryGuest = guests.find(g => g.isPrimary);
  const additionalGuests = guests.filter(g => !g.isPrimary);

  // Who pays (PMS slice 3b): "Credit — booking only (MakeMyTrip)".
  const getSettlementDisplay = (): string => {
    const mode = header.settlementMode || 'GUEST';
    if (mode === 'GUEST') return settlementModeLabel(mode);
    const party = header.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT ? header.agentName
      : header.bookingSource === BOOKING_SOURCE.CORPORATE ? header.corporateName
        : header.otaDisplayName || header.otaName;
    return party ? `${settlementModeLabel(mode)} (${party})` : settlementModeLabel(mode);
  };

  // Source display
  const getSourceDisplay = (): string => {
    if (header.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT && header.agentName) return `Travel Agent — ${header.agentCode ? header.agentCode + ' - ' : ''}${header.agentName}`;
    if (header.bookingSource === BOOKING_SOURCE.CORPORATE && header.corporateName) return `Corporate — ${header.corporateCode ? header.corporateCode + ' - ' : ''}${header.corporateName}`;
    if (header.bookingSource === BOOKING_SOURCE.OTA) {
      const otaLabel = (header as any).otaDisplayName || header.otaName;
      if (otaLabel) return `OTA — ${(header as any).otaCode ? (header as any).otaCode + ' - ' : ''}${otaLabel}${header.otaBookingRef ? ' (Ref: ' + header.otaBookingRef + ')' : ''}`;
    }
    return header.bookingSource || '-';
  };

  // Financial calculations
  const roomCharges = header.roomCharges ?? detailLines.reduce((sum, d) => sum + (d.totalRoomCharges ?? 0), 0);
  const mealCharges = header.mealCharges ?? detailLines.reduce((sum, d) => sum + (d.totalMealCharges ?? 0), 0);
  const extraCharges = header.extraCharges ?? detailLines.reduce((sum, d) => sum + (d.totalExtraCharges ?? 0), 0);
  const subtotal = header.subtotal ?? (roomCharges + mealCharges + extraCharges);
  // Component-wise rate adjustments (INTERNAL view — the full Standard /
  // Adjustment / Selling split; guest documents never show this).
  // Without these rows the summary read rack Subtotal against an adjusted Net,
  // so the ladder visibly failed to reconcile.
  const adjustmentTotal = header.adjustmentTotal
    ?? (fullData?.adjustments ?? []).reduce((sum, a) => sum + (a.adjustmentAmount ?? 0), 0);
  const adjustmentPremium = (fullData?.adjustments ?? [])
    .filter(a => (a.adjustmentAmount ?? 0) > 0)
    .reduce((sum, a) => sum + a.adjustmentAmount, 0);
  const adjustmentDiscount = -(fullData?.adjustments ?? [])
    .filter(a => (a.adjustmentAmount ?? 0) < 0)
    .reduce((sum, a) => sum + a.adjustmentAmount, 0);
  const hasAdjustments = adjustmentPremium > 0 || adjustmentDiscount > 0;
  const sellingSubtotal = subtotal + adjustmentTotal;
  const taxAmount = header.taxAmount ?? 0;
  const grandTotal = header.grandTotal ?? (subtotal + taxAmount);
  // Net amount falls back to grand total for legacy bookings created before the
  // discount columns existed.
  const discountAmount = header.discountAmount ?? 0;
  const netAmount = header.netAmount ?? grandTotal;
  const hasDiscount = (discountAmount > 0) || !!header.isComplimentary;
  // Ancillary add-ons (spa/flowers/etc.) — outside the discount pipeline.
  // Prefer the server-stored aggregate; fall back to summing the loaded lines
  // for legacy bookings where the column hadn't been backfilled.
  const addonTotal = header.addonTotal ?? addons.reduce((sum, a) => sum + (a.lineAmount ?? 0), 0);
  const hasAddons = addons.length > 0 || addonTotal > 0;
  // finalGrandTotal = netAmount + addonTotal — pre-migration bookings without
  // the column fall back to netAmount.
  const finalGrandTotal = header.finalGrandTotal ?? (netAmount + addonTotal);
  const advanceReceived = header.advanceReceived ?? 0;
  const balanceDue = header.balanceDue ?? (finalGrandTotal - advanceReceived);

  // Derive tax indicator from detail lines
  const taxIndicator = detailLines.length > 0
    ? (detailLines.every(d => d.includeTaxes === true) ? 'Tax Inclusive' : 'Tax Exclusive')
    : null;

  // GST breakdown from the booking's tax snapshot. Bookings priced without the
  // Tax Engine have no tax lines and keep the single Tax row.
  const taxBreakdown = buildBookingTaxBreakdown(fullData?.taxLines);
  const showChargeTax = taxBreakdown.charges.length > 0 || taxBreakdown.chargesIncluded.length > 0;
  const showAddonTax = taxBreakdown.addons.length > 0 || taxBreakdown.addonsIncluded.length > 0;
  // Net Total = charges after discount + the tax added on top of them.
  const chargesAfterDiscount = netAmount - taxBreakdown.chargesTaxTotal;

  // Edit is intentionally locked once the guest is in-house (CHECKED_IN) — at
  // that point the booking is anchored to physical realities (assigned rooms
  // in tbl_checkin_room, advance collected, actual_check_in_at). Mutations
  // belong on the folio / stay flows, not on the booking itself. Terminal
  // statuses are excluded too.
  const isEditable = !['CHECKED_IN', 'CHECKED_OUT', 'CANCELLED', 'NO_SHOW'].includes(header.bookingStatusCode);
  const isDownloadable = ['CONFIRMED', 'TENTATIVE'].includes(header.bookingStatusCode);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto', maxWidth: '900px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Booking Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <ModalLoadingSkeleton sections={3} fieldsPerRow={4} showTable tableRows={3} tableCols={6} />
          ) : (
          <>
          {/* === Section 1: Booking Header === */}
          <SectionHeader title="Booking Information" first />
          <div className="form-grid-3">
            <ReadOnlyField label="Booking Number" value={header.bookingNumber} />
            <div>
              <label className="form-label">Booking Status</label>
              <div style={{ padding: '0.5rem' }}>
                <span className={`badge ${getStatusBadgeClass(header.bookingStatusCode)}`}>
                  {header.bookingStatus || header.bookingStatusCode}
                </span>
              </div>
            </div>
            <ReadOnlyField label="Property" value={header.propertyCode ? `${header.propertyCode} - ${header.propertyName}` : header.propertyName} />
          </div>
          <div className="form-grid-3" style={{ marginTop: '1rem' }}>
            <ReadOnlyField label="Source" value={getSourceDisplay()} />
            <ReadOnlyField label="Settlement" value={getSettlementDisplay()} />
            <ReadOnlyField label="Created" value={formatDate(header.createdAt)} />
            <div>
              <label className="form-label">VIP</label>
              <div style={{ padding: '0.5rem' }}>
                <span className={`badge badge-${header.isVip ? 'active' : 'secondary'}`}>
                  {header.isVip ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* === Section 2: Stay Details === */}
          <SectionHeader title="Stay Details" />
          <div className="form-grid-4">
            <ReadOnlyField label="Check-In" value={formatDate(header.checkInDate)} />
            <ReadOnlyField label="Check-Out" value={formatDate(header.checkOutDate)} />
            <ReadOnlyField label="Nights" value={header.numNights} />
            <ReadOnlyField label="Expected Arrival" value={formatTime(header.expectedArrivalTime)} />
          </div>
          <div className="form-grid-3" style={{ marginTop: '1rem' }}>
            <ReadOnlyField label="Total Rooms" value={header.totalRooms} />
            <ReadOnlyField label="Adults" value={header.numAdults} />
            <ReadOnlyField label="Children" value={header.numChildren ?? 0} />
          </div>
          {header.specialNotes && (
            <div style={{ marginTop: '1rem' }}>
              <ReadOnlyField label="Special Notes" value={header.specialNotes} span />
            </div>
          )}

          {/* === Section 3: Room Details === */}
          <SectionHeader title="Room Details" />
          {detailLines.length === 0 && !loading ? (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>No room details available</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-color)' }}>
                    <th style={thStyle}>Room Category</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Rooms</th>
                    <th style={thStyle}>Rate Plan</th>
                    <th style={thStyle}>Meal Plan</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Rate/Room</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Adults</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Children</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLines.map((line, idx) => (
                    <tr key={line.bookingDetailId || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={tdStyle}>
                        {line.categoryCode ? `${line.categoryCode} - ${line.categoryName}` : line.categoryName}
                        {line.allocatedRoomCategoryId && line.allocatedRoomCategoryId !== line.roomCategoryId && (
                          <span
                            title={`Room upgrade: guest is hosted in ${line.allocatedCategoryName} at the ${line.categoryName} rate`}
                            style={{
                              display: 'inline-block', marginLeft: '0.4rem', padding: '0.05rem 0.45rem',
                              borderRadius: '999px', fontSize: '0.68rem', fontWeight: 600,
                              background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                              border: '1px solid var(--border-color)',
                            }}
                          >
                            ↑ {line.allocatedCategoryName}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{line.numberOfRooms}</td>
                      <td style={tdStyle}>{line.rateName || '-'}</td>
                      <td style={tdStyle}>{line.mealPlanName || '-'}</td>
                      {/* OFFERED rate is the headline; rack sits beneath it so
                          internal staff can see both without arithmetic. */}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {formatCurrency(line.offeredRatePerRoom ?? line.ratePerRoom)}
                        {(line.offeredRatePerRoom ?? line.ratePerRoom) !== line.ratePerRoom && (
                          <div style={{
                            fontSize: '0.68rem', color: 'var(--text-muted)',
                            textDecoration: 'line-through', fontWeight: 400,
                          }} title="Standard (rack) rate">
                            {formatCurrency(line.ratePerRoom)}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{line.numAdults}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{line.numChildren ?? 0}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>
                        {formatCurrency(line.sellingLineTotal ?? line.lineTotal)}
                        {(line.adjustmentAmount ?? 0) !== 0 && (
                          <div style={{
                            fontSize: '0.68rem', fontWeight: 600,
                            color: (line.adjustmentAmount ?? 0) < 0 ? '#b91c1c' : '#b45309',
                          }}>
                            {(line.adjustmentAmount ?? 0) > 0 ? '+' : '−'}
                            {formatCurrency(Math.abs(line.adjustmentAmount ?? 0))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* === Section 4: Guest Information === */}
          <SectionHeader title="Guest Information" />
          {primaryGuest && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>Primary Guest</span>
                <span className="badge badge-primary" style={{ fontSize: '0.65rem' }}>Primary</span>
              </div>
              <div className="form-grid-4">
                <ReadOnlyField label="Title" value={primaryGuest.title} />
                <ReadOnlyField label="First Name" value={primaryGuest.firstName} />
                <ReadOnlyField label="Last Name" value={primaryGuest.lastName} />
                <ReadOnlyField label="Type" value={primaryGuest.guestType} />
              </div>
            </div>
          )}
          {/* Linked guest profile info */}
          {header.guestId && (
            <div className="form-grid-3" style={{ marginTop: '0.5rem' }}>
              <ReadOnlyField label="Phone" value={header.guestPhone} />
              <ReadOnlyField label="Email" value={header.guestEmail} />
              <ReadOnlyField label="Guest VIP" value={header.guestIsVip} />
            </div>
          )}
          {additionalGuests.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '0.5rem' }}>Additional Guests</span>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-color)' }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Type</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Age</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Chargeable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalGuests.map((g, idx) => (
                      <tr key={g.id || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={tdStyle}>{`${g.title ? g.title + ' ' : ''}${g.firstName} ${g.lastName || ''}`.trim()}</td>
                        <td style={tdStyle}>{g.guestType}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{g.age ?? '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{g.isChargeable ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {guests.length === 0 && !loading && (
            <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>No guest information available</div>
          )}

          {/* === Section 5: Financial Summary === */}
          <SectionHeader title="Financial Summary" />
          <div style={{ maxWidth: '400px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <SummaryRow label="Room Charges" value={formatCurrency(roomCharges)} />
              <SummaryRow label="Meal Charges" value={formatCurrency(mealCharges)} />
              <SummaryRow label="Extra Charges" value={formatCurrency(extraCharges)} />
              <div style={{ borderTop: '1px solid var(--border-color)', margin: '0.25rem 0' }} />
              <SummaryRow
                label={hasAdjustments ? 'Standard subtotal' : 'Subtotal'}
                value={formatCurrency(subtotal)}
                bold
              />
              {/* Component rate adjustments — the internal Standard →
                  Adjustment → Selling ladder, so the figures reconcile. */}
              {adjustmentPremium > 0 && (
                <SummaryRow label="Rate premiums" value={`+${formatCurrency(adjustmentPremium)}`} accent />
              )}
              {adjustmentDiscount > 0 && (
                <SummaryRow label="Rate discounts" value={`-${formatCurrency(adjustmentDiscount)}`} danger />
              )}
              {hasAdjustments && (
                <SummaryRow label="Selling subtotal" value={formatCurrency(sellingSubtotal)} bold />
              )}
              {taxAmount > 0 && !taxBreakdown.hasLines && <SummaryRow label="Tax" value={formatCurrency(taxAmount)} />}
              {hasDiscount && (
                <>
                  <SummaryRow
                    label={
                      header.isComplimentary
                        ? 'Complimentary booking'
                        : header.discountType === 'Percentage'
                          ? `Discount (${header.discountValue ?? 0}%)`
                          : header.discountType === 'FlatAmount'
                            ? `Discount (Flat ₹${(header.discountValue ?? 0).toLocaleString('en-IN')})`
                            : 'Discount'
                    }
                    value={`-${formatCurrency(discountAmount)}`}
                  />
                  {header.discountRemarks && !header.isComplimentary && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.25rem' }}>
                      {header.discountRemarks}
                    </div>
                  )}
                </>
              )}
              {showChargeTax && (
                <>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '0.25rem 0' }} />
                  <SummaryRow
                    label={taxBreakdown.chargesIncluded.length > 0 ? 'Charges after discount' : 'Taxable Value'}
                    value={formatCurrency(chargesAfterDiscount)}
                  />
                  {taxBreakdown.charges.map((r) => (
                    <SummaryRow key={r.key} label={r.label} value={formatCurrency(r.amount)} />
                  ))}
                  <IncludedTaxRows rows={taxBreakdown.chargesIncluded} />
                </>
              )}
              <div style={{ borderTop: '2px solid var(--border-color)', margin: '0.25rem 0' }} />
              <SummaryRow
                label={hasDiscount ? 'Net Total' : (hasAddons ? 'Net Total' : 'Grand Total')}
                value={formatCurrency(netAmount)}
                bold
                accent
              />
              {hasDiscount && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  Original cost (Subtotal): {formatCurrency(subtotal)} retained for cost reporting.
                </div>
              )}

              {/* Ancillary add-ons — charged in addition to the Net Total and NOT
                  affected by discount. Block hides entirely for bookings without
                  add-ons. */}
              {hasAddons && (
                <>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '0.5rem 0 0.25rem 0' }} />
                  <SummaryRow label="Add-ons" value={formatCurrency(addonTotal)} bold />
                  {addons.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        marginLeft: '0.75rem',
                      }}
                    >
                      <span>{a.addonCode} - {a.addonName} × {a.quantity} @ {formatCurrency(a.unitAmount)}</span>
                      <span>{formatCurrency(a.lineAmount)}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    Discount does not apply to add-ons.
                  </div>
                  {showAddonTax && (
                    <>
                      {taxBreakdown.addons.map((r) => (
                        <SummaryRow key={r.key} label={`Add-on ${r.label}`} value={formatCurrency(r.amount)} />
                      ))}
                      <IncludedTaxRows rows={taxBreakdown.addonsIncluded} />
                    </>
                  )}
                  <div style={{ borderTop: '2px solid var(--border-color)', margin: '0.25rem 0' }} />
                  <SummaryRow
                    label="Grand Total (with Add-ons)"
                    value={formatCurrency(finalGrandTotal)}
                    bold
                    accent
                  />
                </>
              )}
              {header.requiresApproval && (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    padding: '0.125rem 0.5rem',
                    borderRadius: '0.25rem',
                    background: '#fee2e2',
                    color: '#991b1b',
                  }}>Approval Required</span>
                </div>
              )}
              {taxIndicator && (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    padding: '0.125rem 0.5rem',
                    borderRadius: '0.25rem',
                    background: taxIndicator === 'Tax Inclusive' ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-warning-bg, #fef3c7)',
                    color: taxIndicator === 'Tax Inclusive' ? 'var(--badge-active-text, #166534)' : 'var(--badge-warning-text, #92400e)',
                  }}>{taxIndicator}</span>
                </div>
              )}
              <div style={{ borderTop: '1px solid var(--border-color)', margin: '0.25rem 0' }} />
              <SummaryRow label="Advance Received" value={formatCurrency(advanceReceived)} />
              <SummaryRow label="Balance Due" value={formatCurrency(balanceDue)} bold danger={balanceDue > 0} />
              {header.advanceDueDate && (
                <SummaryRow label="Advance Due Date" value={formatDate(header.advanceDueDate)} />
              )}
            </div>
          </div>

          {/* === Section: Payments === */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Payments
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '0.5rem' }}>
                  ({payments.filter(p => p.isActive).length} active{payments.length !== payments.filter(p => p.isActive).length ? `, ${payments.length - payments.filter(p => p.isActive).length} voided` : ''})
                </span>
              </h3>
              {!['CANCELLED', 'CHECKED_OUT', 'NO_SHOW'].includes(header.bookingStatusCode ?? '') && (
                <button
                  className="btn btn-primary"
                  style={{ fontSize: '0.8rem', padding: '0.375rem 0.75rem' }}
                  onClick={() => { setEditPaymentRecord(null); setCaptureModalOpen(true); }}
                >
                  <IconSystem.add size={14} /> Add Advance
                </button>
              )}
            </div>

            {paymentsLoading ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading payments...</div>
            ) : payments.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                No payments recorded for this booking.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Mode</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                      <th style={thStyle}>Reference</th>
                      <th style={thStyle}>Receiving Account</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Bank Verification</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => {
                      const isCheque = p.paymentMode === 'Cheque';
                      const isPending = isCheque && !p.receivingBankAccountId;
                      return (
                        <tr key={p.paymentId} style={{ borderTop: '1px solid var(--border-color)', opacity: p.isActive ? 1 : 0.55 }}>
                          <td style={tdStyle}>{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-IN') : '-'}</td>
                          <td style={tdStyle}>{p.paymentMode}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}>
                            ₹{formatCurrency(p.amount)}
                          </td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{p.paymentReference || '-'}</td>
                          <td style={tdStyle}>
                            {p.receivingBankAccountCode
                              ? `${p.receivingBankAccountCode} (${p.receivingBankName})`
                              : isPending
                                ? <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Pending deposit</span>
                                : '-'
                            }
                          </td>
                          <td style={tdStyle}>
                            {p.isActive
                              ? <span className={`badge badge-${p.paymentStatusColor || 'success'}`} style={{ fontSize: '0.7rem' }}>{p.paymentStatus || p.paymentStatusCode}</span>
                              : <span className="badge badge-inactive" style={{ fontSize: '0.7rem' }}>Voided</span>
                            }
                            {/* Receipt / refund voucher number (slice 4) */}
                            <div style={{ marginTop: '0.2rem' }}>
                              <ReceiptNumber receiptNumber={p.receiptNumber} state={p.receiptNumberState} />
                            </div>
                          </td>
                          <td style={tdStyle}>
                            <PaymentVerificationBadge payment={p} />
                            {p.acctTxnRef && (
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{p.acctTxnRef}</div>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {p.isActive && header.bookingStatusCode !== 'CANCELLED' && (
                              <>
                                <button
                                  className="btn-link"
                                  style={{ fontSize: '0.75rem', marginRight: '0.5rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer' }}
                                  onClick={() => { setEditPaymentRecord(p); setCaptureModalOpen(true); }}
                                  title="Edit payment"
                                >Edit</button>
                                <button
                                  className="btn-link"
                                  style={{ fontSize: '0.75rem', color: 'var(--danger, #dc2626)', background: 'none', border: 'none', cursor: 'pointer' }}
                                  onClick={() => setVoidConfirm({ open: true, payment: p })}
                                  title="Void payment"
                                >Void</button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* === Section 6: Complimentary Info (if applicable) === */}
          {header.isComplimentary && (
            <>
              <SectionHeader title="Complimentary Booking" />
              <div className="form-grid-2">
                <ReadOnlyField label="Complimentary" value="Yes" />
                <ReadOnlyField label="Reason" value={header.complimentaryReason} />
              </div>
            </>
          )}

          {/* === TXN-STAT: Check-In Details (read-only — populated post-check-in) === */}
          {fullData?.checkIn && (
            <>
              <SectionHeader title="Check-In Details" />
              <div className="form-grid-2">
                <ReadOnlyField label="Check-In Date" value={fullData.checkIn.checkInDate?.slice(0, 10)} />
                <ReadOnlyField label="Check-In Time" value={fullData.checkIn.checkInTime?.slice(0, 5)} />
                <ReadOnlyField label="Checked-In By" value={fullData.checkIn.checkedInByName} />
                <ReadOnlyField label="Rooms Assigned" value={fullData.checkIn.rooms?.length ?? 0} />
                {fullData.checkIn.checkInNotes && (
                  <ReadOnlyField label="Notes" value={fullData.checkIn.checkInNotes} span />
                )}
              </div>
              {fullData.checkIn.rooms?.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  Assigned rooms: <strong style={{ color: 'var(--text-primary)' }}>
                    {fullData.checkIn.rooms.map(r => r.roomNumber).join(', ')}
                  </strong>
                </div>
              )}

              {/* ID Documents — read-side list of every guest doc captured for
                  this booking. Falls back to the legacy single-doc fields on
                  fullData.checkIn when the documents array is empty (covers
                  older check-ins captured before the multi-doc release). */}
              {(() => {
                const docs = fullData.checkIn.documents && fullData.checkIn.documents.length > 0
                  ? fullData.checkIn.documents.map((d) => ({
                      key: d.id,
                      type: d.documentType,
                      number: d.documentNumber,
                      path: d.documentPath,
                    }))
                  : fullData.checkIn.idDocumentPath
                    ? [{
                        key: 'primary',
                        type: fullData.checkIn.idType,
                        number: fullData.checkIn.idNumber,
                        path: fullData.checkIn.idDocumentPath,
                      }]
                    : [];

                if (docs.length === 0) return null;

                return (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' }}>
                      ID Documents ({docs.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                      {docs.map((d) => (
                        <div
                          key={d.key}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '120px 1fr auto',
                            gap: '0.5rem',
                            alignItems: 'center',
                            padding: '0.375rem 0.625rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            background: 'var(--bg-secondary)',
                            fontSize: '0.8125rem',
                          }}
                        >
                          <strong style={{ color: 'var(--text-primary)' }}>{d.type || '—'}</strong>
                          <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={d.number || ''}>
                            {d.number ? `# ${d.number}` : <em>(no number captured)</em>}
                          </span>
                          {d.path ? (
                            <button
                              type="button"
                              onClick={() => setIdDocPreviewPath(d.path!)}
                              className="btn btn-secondary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                              title="Preview the captured document"
                            >
                              <IconSystem.eye size={14} /> View
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>no file</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* === TXN-STAT: Check-Out Details (read-only — populated post-check-out) === */}
          {fullData?.checkOut && (
            <>
              <SectionHeader title="Check-Out Details" />
              <div className="form-grid-2">
                <ReadOnlyField label="Check-Out Date" value={fullData.checkOut.checkOutDate?.slice(0, 10)} />
                <ReadOnlyField label="Check-Out Time" value={fullData.checkOut.checkOutTime?.slice(0, 5)} />
                <ReadOnlyField label="Checked-Out By" value={fullData.checkOut.checkedOutByName} />
                <ReadOnlyField label="Total Bill" value={formatCurrency(fullData.checkOut.totalBill)} />
                <ReadOnlyField label="Advance Paid" value={formatCurrency(fullData.checkOut.advancePaid ?? 0)} />
                <ReadOnlyField label="Balance Collected" value={formatCurrency(fullData.checkOut.balanceCollected ?? 0)} />
                {fullData.checkOut.paymentMode && (
                  <ReadOnlyField label="Payment Mode" value={fullData.checkOut.paymentMode} />
                )}
                {fullData.checkOut.paymentRef && (
                  <ReadOnlyField label="Payment Reference" value={fullData.checkOut.paymentRef} />
                )}
                {fullData.checkOut.checkOutNotes && (
                  <ReadOnlyField label="Notes" value={fullData.checkOut.checkOutNotes} span />
                )}
              </div>
              {/* Optional breakdown */}
              {(fullData.checkOut.billRoom != null
                || fullData.checkOut.billRestaurant != null
                || fullData.checkOut.billLaundry != null
                || fullData.checkOut.billSpa != null
                || fullData.checkOut.billOther != null) && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem 0.625rem',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Bill Breakdown</div>
                  {fullData.checkOut.billRoom != null && <div>Room: <strong>{formatCurrency(fullData.checkOut.billRoom)}</strong></div>}
                  {fullData.checkOut.billRestaurant != null && <div>Restaurant: <strong>{formatCurrency(fullData.checkOut.billRestaurant)}</strong></div>}
                  {fullData.checkOut.billLaundry != null && <div>Laundry: <strong>{formatCurrency(fullData.checkOut.billLaundry)}</strong></div>}
                  {fullData.checkOut.billSpa != null && <div>Spa: <strong>{formatCurrency(fullData.checkOut.billSpa)}</strong></div>}
                  {fullData.checkOut.billOther != null && (
                    <div>
                      Other: <strong>{formatCurrency(fullData.checkOut.billOther)}</strong>
                      {fullData.checkOut.billOtherDesc && <> — <em>{fullData.checkOut.billOtherDesc}</em></>}
                    </div>
                  )}
                  {fullData.checkOut.billSubtotal != null && <div>Subtotal: <strong>{formatCurrency(fullData.checkOut.billSubtotal)}</strong></div>}
                  {fullData.checkOut.billTaxes != null && <div>Taxes: <strong>{formatCurrency(fullData.checkOut.billTaxes)}</strong></div>}
                  {fullData.checkOut.billGrandTotal != null && <div>Grand Total: <strong>{formatCurrency(fullData.checkOut.billGrandTotal)}</strong></div>}
                </div>
              )}
            </>
          )}

          {/* === Section 7: Terms & Policies (from booking snapshot) === */}
          {header.bookingTermsSnapshot && (
            <>
              <SectionHeader title="Terms & Policies" />
              <div style={{
                padding: '0.75rem',
                background: 'var(--bg-secondary)',
                borderRadius: '0.375rem',
                border: '1px solid var(--form-border-color)',
                whiteSpace: 'pre-line',
                lineHeight: '1.6',
                fontSize: '0.85rem',
                maxHeight: '200px',
                overflowY: 'auto',
              }}>
                {header.bookingTermsSnapshot}
              </div>
            </>
          )}
          </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {isDownloadable && (
            <button
              className="btn btn-secondary"
              onClick={handleDownloadPdf}
              style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
              title="Preview the confirmation, then download"
            >
              <IconSystem.download size={16} />
              Preview / Download
            </button>
          )}
          {/* The registration card (GRC-2); the server checks Front Office / In-House view. */}
          {['CHECKED_IN', 'CHECKED_OUT'].includes(header.bookingStatusCode ?? '') && (
            <button
              className="btn btn-secondary"
              onClick={() => { void openPdf(() => outputProfileService.grcPdf(header.bookingId), 'The registration card did not print.'); }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
              title="The pre-filled registration card for the guest to complete and sign"
            >
              <IconSystem.print size={16} />
              Print GRC
            </button>
          )}
          {onEdit && isEditable && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(header); }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Payment capture modal — opened from the Payments section */}
      {booking && (
        <PaymentCaptureModal
          isOpen={captureModalOpen}
          mode={editPaymentRecord ? 'edit' : 'create'}
          collectionPoint={collectionPoint}
          bookingId={booking.bookingId}
          propertyId={booking.propertyId}
          bookingNumber={booking.bookingNumber}
          suggestedAmount={
            editPaymentRecord
              ? undefined
              // Default = outstanding advance (advanceRequired − advanceReceived),
              // not the full net balance. Cashier can edit if guest pays more.
              : (() => {
                  const b = fullData?.booking ?? booking;
                  const out = (b.advanceRequired ?? 0) - (b.advanceReceived ?? 0);
                  return Math.max(0, out) || undefined;
                })()
          }
          record={editPaymentRecord}
          onSave={handlePaymentSaved}
          onClose={() => { setCaptureModalOpen(false); setEditPaymentRecord(null); }}
          tabId={tabId}
          moduleName={moduleName}
        />
      )}

      {/* PDF preview — opened from the Preview / Download button */}
      {booking && (
        <BookingPdfPreviewModal
          isOpen={pdfPreviewOpen}
          bookingId={booking.bookingId}
          bookingNumber={booking.bookingNumber}
          onClose={() => setPdfPreviewOpen(false)}
        />
      )}

      {/* In-app ID document preview — replaces the old `<a target="_blank">`
          that triggered MISSING_TENANT_ID by issuing an unauthenticated
          browser request. The shared component fetches the bytes through
          axios (full headers attached) and renders the image / PDF
          in-place. Closes on Esc or the Close button. */}
      {idDocPreviewPath && (
        <FilePreviewModal
          relativePath={idDocPreviewPath}
          onClose={() => setIdDocPreviewPath(null)}
        />
      )}

      {/* Void confirmation */}
      <ConfirmDialog
        open={voidConfirm.open}
        title="Void Payment"
        message={
          voidConfirm.payment
            ? `Voiding will reduce the booking's advance received by ₹${formatCurrency(voidConfirm.payment.amount)}. Continue?`
            : ''
        }
        confirmLabel="Void"
        cancelLabel="Cancel"
        variant="danger"
        loading={isVoiding}
        loadingMessage="Voiding payment..."
        onConfirm={handleVoidPayment}
        onCancel={() => setVoidConfirm({ open: false, payment: null })}
      />
    </>
  );
};

// ============================================================================
// SUMMARY ROW SUB-COMPONENT
// ============================================================================

/** Tax already inside tax-inclusive rates — shown for information, never added to a total. */
const IncludedTaxRows: React.FC<{ rows: TaxBreakdownRow[] }> = ({ rows }) => (
  <>
    {rows.map((r) => (
      <div
        key={r.key}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: 'var(--text-secondary)',
          marginLeft: '0.75rem',
        }}
      >
        <span>Includes {r.label}</span>
        <span>{formatCurrency(r.amount)}</span>
      </div>
    ))}
  </>
);

const SummaryRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  accent?: boolean;
  danger?: boolean;
}> = ({ label, value, bold, accent, danger }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
    <span style={{ color: 'var(--text-muted)', fontWeight: bold ? 600 : 400 }}>{label}</span>
    <span style={{
      fontWeight: bold ? 700 : 400,
      color: danger ? 'var(--danger, #dc2626)' : accent ? 'var(--primary)' : 'var(--text-primary)',
      fontSize: bold ? '0.9rem' : '0.85rem',
    }}>{value}</span>
  </div>
);

// ============================================================================
// TABLE STYLES
// ============================================================================

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  color: 'var(--text-primary)',
  whiteSpace: 'nowrap',
};

export default BookingViewModal;
